import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { lagerFinishedProductsAPI, lagerProductionAPI, lagerTransportAPI } from '../services/api';
import { LagerCurrentStatusRow, LagerFinishedProduct, LagerProductionRecord, LagerTransport } from '../types';
import { showCustomAlert } from './CustomAlert';

function round2(n: number) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function isEffectivelyZero(n: number) {
  return Math.abs(n) < 0.005;
}

function formatNumber(n: number, opts?: { showPlus?: boolean }) {
  if (isEffectivelyZero(n)) return '0.00';
  const v = round2(n);
  const s = v.toFixed(2);
  if (opts?.showPlus && v > 0) return `+${s}`;
  return s;
}

function valueStyle(value: number) {
  const abs = Math.abs(value);
  // Neutral for ~0
  if (isEffectivelyZero(value)) {
    return { color: '#64748b', fontWeight: 500 as const };
  }

  // Magnitude tiers (kept simple + readable)
  // 0–10: light, 10–100: medium, 100–1000: strong, 1000+: strongest
  const tier = abs >= 1000 ? 4 : abs >= 100 ? 3 : abs >= 10 ? 2 : 1;

  if (value < 0) {
    const color = tier === 4 ? '#991b1b' : tier === 3 ? '#dc2626' : tier === 2 ? '#f97316' : '#f59e0b';
    return { color, fontWeight: tier >= 3 ? (700 as const) : (600 as const) };
  }

  const color = tier === 4 ? '#166534' : tier === 3 ? '#15803d' : tier === 2 ? '#16a34a' : '#22c55e';
  return { color, fontWeight: tier >= 3 ? (700 as const) : (600 as const) };
}

const LagerCurrentStatus: React.FC = () => {
  const [rows, setRows] = useState<LagerCurrentStatusRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [products, production, transports] = await Promise.all([
        lagerFinishedProductsAPI.getAll(),
        lagerProductionAPI.getAll({ showAll: true }),
        lagerTransportAPI.getAll()
      ]);

      const status = computeStatus(products, production, transports);
      setRows(status);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju stanja';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportPdf = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text('Trenutno stanje proizvoda', 14, 16);
      doc.setFontSize(10);
      doc.text(`Datum: ${new Date().toLocaleDateString('sr-RS')}`, 14, 22);

      autoTable(doc, {
        startY: 28,
        head: [['Proizvod', 'Trenutno (kom)', 'Trenutno (paleta)', 'Trenutno (m²)']],
        body: rows.map((r) => [
          r.productName,
          formatNumber(r.currentKom, { showPlus: true }),
          formatNumber(r.currentPaleta, { showPlus: true }),
          formatNumber(r.currentM2, { showPlus: true })
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] }
      });

      doc.save(`trenutno-stanje-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e: any) {
      showCustomAlert.error(e?.message || 'Greška pri exportu PDF-a');
    }
  };

  const numberBody = (key: 'currentKom' | 'currentPaleta' | 'currentM2') => (row: LagerCurrentStatusRow) => {
    const val = row[key];
    return <span style={valueStyle(val)}>{formatNumber(val, { showPlus: true })}</span>;
  };

  const computedSubtitle = useMemo(() => 'Trenutno stanje = proizvedeno − transportovano (preračunato iz m²)', []);

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Trenutno stanje proizvoda" subTitle={computedSubtitle}>
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex justify-content-start gap-2" style={{ marginBottom: '1rem' }}>
          <Button label="Exportuj PDF" icon="pi pi-file-pdf" onClick={exportPdf} severity="secondary" />
          <Button label="Osveži" icon="pi pi-refresh" onClick={loadData} severity="secondary" outlined />
        </div>

        <DataTable value={rows} loading={loading} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} responsiveLayout="scroll">
          <Column field="productName" header="Proizvod" sortable />
          <Column header="Trenutno (kom)" body={numberBody('currentKom')} />
          <Column header="Trenutno (paleta)" body={numberBody('currentPaleta')} />
          <Column header="Trenutno (m²)" body={numberBody('currentM2')} />
        </DataTable>
      </Card>
    </div>
  );
};

function computeStatus(
  products: LagerFinishedProduct[],
  production: LagerProductionRecord[],
  transports: LagerTransport[]
): LagerCurrentStatusRow[] {
  // Canonical value is m². kom/paleta are derived from m² using product definition.
  const m2ByProduct = new Map<string, { productId: string; productName: string; currentM2: number }>();
  const productById = new Map<string, LagerFinishedProduct>();

  for (const p of products) {
    productById.set(p._id, p);
    m2ByProduct.set(p._id, { productId: p._id, productName: p.name, currentM2: 0 });
  }

  // Production adds (m² already computed on backend):
  for (const rec of production) {
    const row =
      m2ByProduct.get(rec.productId) ||
      { productId: rec.productId, productName: rec.productName, currentM2: 0 };
    row.currentM2 += Number(rec.m2Total || 0);
    m2ByProduct.set(rec.productId, row);
  }

  // Transport subtracts:
  for (const t of transports) {
    const productId =
      t.originalProductId ||
      (typeof t.productId === 'string' ? t.productId : t.productId?._id) ||
      '';
    if (!productId) continue;

    const row =
      m2ByProduct.get(productId) ||
      {
        productId,
        productName: (typeof t.productId !== 'string' ? t.productId?.name : t.productName) || 'Proizvod',
        currentM2: 0
      };

    const qty = Number(t.quantity || 0);
    const unit = t.unit;
    const product = productById.get(productId);

    // Prefer persisted squareMeters if present; otherwise compute from product definition.
    let m2 = Number(t.squareMeters ?? 0);
    if (!m2 && unit === 'm2') m2 = qty;
    if ((!m2 || isEffectivelyZero(m2)) && product) {
      const m2PerPiece = Number(product.m2PerPiece || 0);
      const perPallet = Number(product.perPallet || 0);
      if (unit === 'kom' && m2PerPiece) m2 = qty * m2PerPiece;
      if (unit === 'paleta' && m2PerPiece && perPallet) m2 = qty * perPallet * m2PerPiece;
    }

    row.currentM2 -= Number(m2 || 0);
    m2ByProduct.set(productId, row);
  }

  // Derive kom/paleta from m² using product definition.
  const out: LagerCurrentStatusRow[] = [];
  for (const row of Array.from(m2ByProduct.values())) {
    const product = productById.get(row.productId);
    const m2PerPiece = Number(product?.m2PerPiece || 0);
    const perPallet = Number(product?.perPallet || 0);
    const m2PerPallet = m2PerPiece && perPallet ? m2PerPiece * perPallet : 0;

    const currentKom = m2PerPiece ? row.currentM2 / m2PerPiece : 0;
    const currentPaleta = m2PerPallet ? row.currentM2 / m2PerPallet : 0;

    out.push({
      productId: row.productId,
      productName: row.productName,
      currentKom,
      currentPaleta,
      currentM2: row.currentM2
    });
  }

  return out.sort((a, b) => a.productName.localeCompare(b.productName, 'sr'));
}

export default LagerCurrentStatus;


